// Full trade journal, cursor-paginated via the server's `before_id`.
// Rendered inside a card owned by HistoryPage (which holds the sleeve
// filter). Sleeve filter is applied client-side on the loaded pages
// (a preset can map to N algo_ids; aggregates stay server-exact).

import { useInfiniteQuery } from "@tanstack/react-query";
import { journal as journalApi } from "../api/endpoints";
import type { JournalSource, TradeJournalEntry } from "../api/types";
import { usd, clock, day } from "../lib/format";

interface Props {
  source?: JournalSource; // undefined = all sources
  since?: string;
  algoPreset?: string; // undefined = all sleeves
  accountId?: number; // undefined = all accounts
}

const PAGE = 200;

function sourcePill(s: JournalSource): string {
  if (s === "sim") return "pill kind";
  if (s === "live") return "pill good";
  return "pill dim";
}

export function JournalTable({ source, since, algoPreset, accountId }: Props) {
  const q = useInfiniteQuery({
    queryKey: ["journal", "list", source ?? "all", since ?? "all", accountId],
    queryFn: ({ pageParam }) =>
      journalApi.list({ source, since, before_id: pageParam, limit: PAGE, account_id: accountId }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.length >= PAGE ? lastPage.at(-1)?.id : undefined,
    refetchInterval: 30_000,
  });

  const loaded: TradeJournalEntry[] = q.data?.pages.flat() ?? [];
  const rows = algoPreset ? loaded.filter((r) => r.algo_preset === algoPreset) : loaded;

  if (q.isLoading) return <div className="muted small">loading journal…</div>;
  if (rows.length === 0) return <div className="muted small">no trades match the current filters</div>;

  return (
    <>
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Entry</th>
            <th>Exit</th>
            <th>Sleeve</th>
            <th>Mkt</th>
            <th>Side</th>
            <th className="num">Qty</th>
            <th className="num">In</th>
            <th className="num">Out</th>
            <th className="num">Net</th>
            <th className="num">MAE/MFE</th>
            <th>Reason</th>
            <th>Regime</th>
            <th>Src</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => {
            const sideLower = t.side.toLowerCase();
            const isLong = sideLower.startsWith("b") || sideLower === "long";
            const open = t.exit_ts == null;
            const pnlCls = (t.pnl_net ?? 0) > 0 ? "good" : (t.pnl_net ?? 0) < 0 ? "bad" : "";
            return (
              <tr key={t.id}>
                <td title={t.entry_ts}>{day(t.entry_ts)}</td>
                <td className="mono">{clock(t.entry_ts)}</td>
                <td className="mono muted">{t.exit_ts ? clock(t.exit_ts) : "—"}</td>
                <td><span className="mono">{t.algo_preset}</span></td>
                <td className="muted">{t.market}</td>
                <td className={isLong ? "good" : "bad"}>{isLong ? "Long" : "Short"}</td>
                <td className="num">{t.qty}</td>
                <td className="num">{t.entry_price.toFixed(2)}</td>
                <td className="num">{t.exit_price != null ? t.exit_price.toFixed(2) : "—"}</td>
                <td className={"num " + pnlCls}>{open ? "—" : usd(t.pnl_net, { sign: true })}</td>
                <td className="num muted small">
                  {t.mae_points != null ? t.mae_points.toFixed(1) : "—"}/
                  {t.mfe_points != null ? t.mfe_points.toFixed(1) : "—"}
                </td>
                <td className="muted small">{t.exit_reason ?? (open ? "open" : "")}</td>
                <td>{t.regime_tag ? <span className="pill dim">{t.regime_tag}</span> : ""}</td>
                <td><span className={sourcePill(t.source)}>{t.source}</span></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="row" style={{ marginTop: 10 }}>
        <span className="muted small">{rows.length} shown</span>
        <div className="spacer" />
        {q.hasNextPage && (
          <button onClick={() => void q.fetchNextPage()} disabled={q.isFetchingNextPage}>
            {q.isFetchingNextPage ? "loading…" : "Load more"}
          </button>
        )}
      </div>
    </>
  );
}
